import { useRef, useState, useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import BoundingBoxLayer from './BoundingBoxLayer';
import { visibleLayersAtom, selectedFindingIdAtom } from '@/hooks/atoms';
import { SEVERITY_COLORS, type Finding } from '@/types/forensics';

interface VideoViewerWithOverlayProps {
  findings: Finding[];
  previewUrl: string;
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default function VideoViewerWithOverlay({ findings, previewUrl }: VideoViewerWithOverlayProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [layers] = useAtom(visibleLayersAtom);
  const [, setSelectedFinding] = useAtom(selectedFindingIdAtom);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const showBoxes = layers.has('bounding_boxes');

  // Track container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setDuration(video.duration * 1000);
      setVideoSize({ width: video.videoWidth, height: video.videoHeight });
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setCurrentTimeMs(video.currentTime * 1000);
    }
  }, []);

  // Compute rendered video dimensions (fit within container)
  const aspect = videoSize.width && videoSize.height ? videoSize.width / videoSize.height : 16 / 9;
  let displayWidth = containerSize.width;
  let displayHeight = containerSize.width / aspect;
  if (displayHeight > containerSize.height - 60) {
    displayHeight = containerSize.height - 60; // Reserve space for timeline
    displayWidth = displayHeight * aspect;
  }

  // Get findings that are active at current time
  const activeFindings = findings.filter((f) => {
    if (f.timeSpan) {
      return currentTimeMs >= f.timeSpan.startMs && currentTimeMs <= f.timeSpan.endMs;
    }
    // Findings without timeSpan are always visible (global or single-frame)
    return true;
  });

  // Get all findings with time spans for timeline markers
  const timelineFindings = findings.filter((f) => f.timeSpan);

  // Jump to finding's timecode when clicked
  const handleTimelineMarkerClick = useCallback(
    (finding: Finding) => {
      const video = videoRef.current;
      if (video && finding.timeSpan) {
        video.currentTime = finding.timeSpan.startMs / 1000;
        setSelectedFinding(finding.id);
      }
    },
    [setSelectedFinding],
  );

  return (
    <div className="relative flex flex-col h-full" ref={containerRef}>
      {/* Video area */}
      <div className="flex-1 overflow-hidden bg-black flex items-center justify-center">
        <div className="relative" style={{ width: displayWidth, height: displayHeight }}>
          <video
            ref={videoRef}
            src={previewUrl}
            controls
            className="block w-full h-full"
            style={{ position: 'relative', zIndex: 1 }}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
          />

          {/* Bounding box overlay — pointer-events: none so video controls remain clickable */}
          {showBoxes && displayWidth > 0 && (
            <div className="absolute top-0 left-0 right-0" style={{ height: displayHeight * 0.75, pointerEvents: 'none' }}>
              <BoundingBoxLayer findings={activeFindings} containerWidth={displayWidth} containerHeight={displayHeight * 0.75} />
            </div>
          )}
        </div>
      </div>

      {/* Timeline with anomaly markers */}
      {duration > 0 && timelineFindings.length > 0 && (
        <div className="px-4 py-2 bg-card border-t">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-muted-foreground">{formatTime(currentTimeMs)}</span>
            <div className="flex-1 relative h-6">
              {/* Timeline bar */}
              <div className="absolute top-2.5 left-0 right-0 h-1 bg-muted rounded-full" />

              {/* Playback position */}
              <div
                className="absolute top-2 h-2 w-2 bg-primary rounded-full -translate-x-1/2"
                style={{ left: `${(currentTimeMs / duration) * 100}%` }}
              />

              {/* Finding markers */}
              {timelineFindings.map((finding) => {
                if (!finding.timeSpan) return null;
                const startPct = (finding.timeSpan.startMs / duration) * 100;
                const widthPct = ((finding.timeSpan.endMs - finding.timeSpan.startMs) / duration) * 100;
                const color = SEVERITY_COLORS[finding.severity];

                return (
                  <button
                    key={finding.id}
                    className="absolute top-1 h-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                    style={{
                      left: `${startPct}%`,
                      width: `${Math.max(widthPct, 0.5)}%`,
                      backgroundColor: color,
                    }}
                    title={`${finding.title} (${formatTime(finding.timeSpan.startMs)} - ${formatTime(finding.timeSpan.endMs)})`}
                    onClick={() => handleTimelineMarkerClick(finding)}
                  />
                );
              })}
            </div>
            <span className="text-xs text-muted-foreground">{formatTime(duration)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {timelineFindings.length} temporal finding{timelineFindings.length !== 1 ? 's' : ''} — click markers to jump
          </p>
        </div>
      )}
    </div>
  );
}
